import { Chord, type Line } from 'chordsheetjs';
import type { Progression, SectionKind } from './types.ts';

// Krok (D) potoku (część): normalizacja akordów, najmniejszy okres, kotwica,
// sygnatura i dziedziczenie progresji. Czyste funkcje na tablicach stringów
// (plus jeden most od `Line[]` biblioteki do surowych tokenów akordów), bez
// udziału DOM, żeby dało się je testować w izolacji od renderera.
//
// `Progression.raw` niesie PEŁNY ciąg akordów, z powtórzeniami sąsiednimi.
// To nie jest pole debugowe: z niego liczy się `signature` (w `buildSignature`,
// raz, zamrożona przez wywołującego) i z niego korzysta poziom degradacji 4.
// Zwijanie sąsiadujących powtórzeń (`dedupeAdjacent`) jest osobnym, świadomie
// PÓŹNIEJSZYM krokiem degradacji, nigdy nie wchodzi na wejściu do
// `findSmallestPeriod`: `A A B | A A B` zwinięte na wejściu stałoby się
// `A B A B`, czyli `A B ×2`, inną progresją o innej długości harmonicznej.

/**
 * Wyciąga surowe tokeny akordów z linii `Song` (biblioteki ChordSheetJS), w
 * kolejności występowania, BEZ zwijania sąsiadujących powtórzeń. Pomija puste
 * pozycje (`chords` puste albo same białe znaki), bo to nie są realne akordy.
 */
export function rawChordTokens(lines: readonly Line[]): string[] {
  const tokens: string[] = [];
  for (const line of lines) {
    for (const item of line.items) {
      if ('chords' in item && typeof item.chords === 'string') {
        const trimmed = item.chords.trim();
        if (trimmed !== '') tokens.push(trimmed);
      }
    }
  }
  return tokens;
}

/**
 * Wyciąga akordy pogrupowane we FRAZY, jedna fraza na linię źródłową `Line`.
 * To jest cała nowość modelu frazowania: autor taba wyrównał każdą linię
 * akordów do jednego wersu tekstu, więc linia źródłowa JEST frazą, za darmo
 * z danych, które i tak już mamy (patrz `Progression.phrases`). Linia bez ani
 * jednego akordu NIE tworzy pustej frazy (nie ląduje w wyniku wcale), zgodnie
 * z zasadą "nie stawiamy fraz przeciw miejscu, pokazujemy je tylko tam, gdzie
 * naprawdę są akordy". `rawChordTokens(lines)` jest zawsze dokładnie równe
 * `rawChordPhrases(lines).flat()` (ta sama logika filtrowania pustych pozycji
 * wewnątrz linii), sprawdzone testem.
 */
export function rawChordPhrases(lines: readonly Line[]): string[][] {
  const phrases: string[][] = [];
  for (const line of lines) {
    const phrase: string[] = [];
    for (const item of line.items) {
      if ('chords' in item && typeof item.chords === 'string') {
        const trimmed = item.chords.trim();
        if (trimmed !== '') phrase.push(trimmed);
      }
    }
    if (phrase.length > 0) phrases.push(phrase);
  }
  return phrases;
}

/** "N.C." (no chord) w dowolnej pisowni, np. "N.C.", "NC", "n.c." - zachowywane
 * dosłownie, nigdy traktowane jako token niesparsowany. */
const NO_CHORD_RE = /^n\.?c\.?$/i;

/** Wynik normalizacji pojedynczego tokenu akordu. */
export interface NormalizedChord {
  /** Token po normalizacji (albo oryginał, gdy nie dało się sparsować). */
  value: string;
  /** true, gdy `Chord.parse` zwrócił `null` (śmieć w źródle, nie akord). */
  unparsed: boolean;
}

/**
 * Normalizuje jeden token akordu przez `Chord.parse` z ChordSheetJS. Gdy parser
 * zwróci `null`, zachowuje surowy token bez zmian (żeby diagnostyka miała co
 * pokazać), zamiast go odrzucać po cichu. "N.C." jest specjalnym przypadkiem:
 * `Chord.parse` go nie rozpoznaje, ale to legalny, świadomy zapis "brak akordu",
 * nie śmieć, więc nie ląduje wśród `unparsedChordTokens`. Bas jest zachowywany
 * przez samo `Chord`, np. `D/F#` wraca jako `D/F#`.
 */
export function normalizeChordToken(token: string): NormalizedChord {
  const trimmed = token.trim();
  if (NO_CHORD_RE.test(trimmed)) return { value: trimmed, unparsed: false };
  const parsed = Chord.parse(trimmed);
  if (parsed) return { value: parsed.toString(), unparsed: false };
  return { value: trimmed, unparsed: true };
}

export interface NormalizedChords {
  /** Znormalizowane tokeny, ta sama długość i kolejność co wejście. */
  tokens: string[];
  /** Surowe tokeny, których `Chord.parse` nie rozpoznał (bez "N.C."). */
  unparsedChordTokens: string[];
}

/** Normalizuje cały ciąg tokenów akordów, patrz {@link normalizeChordToken}. */
export function normalizeChords(rawTokens: readonly string[]): NormalizedChords {
  const tokens: string[] = [];
  const unparsedChordTokens: string[] = [];
  for (const raw of rawTokens) {
    const { value, unparsed } = normalizeChordToken(raw);
    tokens.push(value);
    if (unparsed) unparsedChordTokens.push(raw);
  }
  return { tokens, unparsedChordTokens };
}

/**
 * Usuwa sąsiadujące powtórzenia z ciągu akordów, zachowując kolejność
 * pierwszego wystąpienia każdego biegu. To jest poziom degradacji 4, NIE krok
 * wejściowy: wywoływany dopiero przy renderowaniu okrojonego wariantu, nigdy
 * przed `findSmallestPeriod`. Świadomy koszt: gubimy informację "Am przez dwa
 * takty", ale `Progression.raw` (pełny ciąg) pozostaje dostępny osobno.
 */
export function dedupeAdjacent(chords: readonly string[]): string[] {
  const result: string[] = [];
  for (const chord of chords) {
    if (result.length === 0 || result[result.length - 1] !== chord) result.push(chord);
  }
  return result;
}

/**
 * Znajduje najmniejszy okres RAW ciągu akordów (z powtórzeniami sąsiednimi, BEZ
 * uprzedniego `dedupeAdjacent`) i buduje `Progression`. Kandydat na okres `p`
 * musi spełniać `seq[i] === seq[i % p]` dla KAŻDEGO `i` aż po ogon (nie
 * wystarczy podzielność `n % p`, inaczej ogon niepasujący do okresu, jak w
 * `A B A B C`, przeszedłby fałszywie), i muszą wystąpić co najmniej dwa pełne
 * okresy (`floor(n / p) >= 2`, inaczej `A B A` wyszłoby jako fałszywe `A B ×1`).
 * Gdy żaden okres krótszy od całości nie pasuje, zwraca `times === 1` i pełny
 * ciąg jako `base`, bez zwinięcia.
 *
 * `sourceTimes`, gdy podane (autor taba napisał wprost `x4` nad wersem),
 * wygrywa nad WNIOSKOWANYM `times`, nigdy nad wykrytym `base`/`tail`: okres
 * jest zawsze szukany normalnie (autor mógł napisać "x4" nad ciągiem, który
 * sam w sobie już się powtarza, np. dwuakordowa fraza zapisana dwukrotnie z
 * adnotacją "x4" oznaczającą cztery powtórzenia przy graniu), a nadpisywany
 * jest tylko finalny `times`. Gdy w ciągu nie ma żadnego wykrywalnego okresu
 * (typowy przypadek: krótka fraza pokazana w tabie RAZ, z adnotacją "x4" przy
 * niej), wykrywanie i tak zwraca `base` równe całemu ciągowi, więc efekt jest
 * dokładnie taki, jak dla testu 7 z planu: `base` = podany ciąg, `tail` puste.
 */
/**
 * Wynik samego wykrywania okresu, BEZ `phrases`: `findSmallestPeriod` operuje
 * na płaskim ciągu i nie wie nic o podziale na frazy (to dokłada dopiero
 * wywołujący, `compile.ts`, przez `{ ...periodResult, phrases }`). Osobny typ
 * od `Progression` celowo, żeby wywołania tej funkcji z płaskim ciągiem "z
 * ręki" (testy jednostkowe okresu) nie musiały fabrykować frazowania, którego
 * nie mają.
 */
export interface PeriodResult {
  raw: readonly string[];
  base: readonly string[];
  times: number;
  tail: readonly string[];
  timesFromSource: boolean;
}

export function findSmallestPeriod(
  chords: readonly string[],
  sourceTimes?: number | null,
): PeriodResult {
  const raw = [...chords];
  const detected = detectPeriod(raw);

  if (sourceTimes != null && sourceTimes >= 1) {
    return { raw, base: detected.base, times: sourceTimes, tail: detected.tail, timesFromSource: true };
  }
  return { raw, ...detected, timesFromSource: false };
}

interface DetectedPeriod {
  base: string[];
  times: number;
  tail: string[];
}

/** Samo wykrywanie najmniejszego okresu, bez `sourceTimes` ani `raw`/`timesFromSource`
 * (dokładane przez wywołującego), żeby `findSmallestPeriod` mogło wykryć okres
 * NIEZALEŻNIE od tego, czy autor podał też własne "xN". */
function detectPeriod(raw: readonly string[]): DetectedPeriod {
  const n = raw.length;
  if (n === 0) return { base: [], times: 1, tail: [] };

  for (let period = 1; period < n; period += 1) {
    if (Math.floor(n / period) < 2) continue;
    let matches = true;
    for (let i = 0; i < n; i += 1) {
      if (raw[i] !== raw[i % period]) {
        matches = false;
        break;
      }
    }
    if (!matches) continue;
    const times = Math.floor(n / period);
    const fullLength = times * period;
    return { base: raw.slice(0, period), times, tail: raw.slice(fullLength) };
  }

  return { base: [...raw], times: 1, tail: [] };
}

const NO_CHORD_WORD_RE = /^\d+$/;
const TAB_REMNANT_RE = /^[A-Ga-g](#|b)?\|/;
const BRACKET_HEADER_LIKE_RE = /^\[.*]$/;
const COLON_HEADER_LIKE_RE = /^[\p{L}][\p{L} \-]*:$/u;
// "1st:", "2nd:" itd.: to samo, co ORDINAL_LINE_RE w preNormalize.ts, ale
// powtórzone lokalnie, bo ten moduł nie zależy od preNormalize. "1st:" w
// nie-placz-ewka.txt jest realnym przykładem tekstu-nagłówka, który nie
// pasuje do COLON_HEADER_LIKE_RE (zaczyna się cyfrą, nie literą).
const ORDINAL_HEADER_LIKE_RE = /^\d+(st|nd|rd|th):$/iu;

// Linia będąca CAŁA notacją powtórzenia, samodzielnie, czasem w nawiasie okrągłym,
// np. "(x2)" - realny defekt wehikul-czasu.txt, sekcja outro: "A E D A" na jednej
// linii, a "(x2)" na kolejnej, osobnej linii (nie doklejone do niczego). To NIE jest
// pułapka 4 biblioteki (tam ogon ląduje BEZ SPACJI na końcu istniejącego tekstu,
// łapie to `stripGluedRepeat` niżej) - to osobna linia, która sama w sobie nie niesie
// żadnego sensownego tekstu do pokazania jako kotwica.
const REPEAT_ONLY_LINE_RE = /^\(?x\d+\)?$/i;

/** Zdejmuje z końca wiersza notację powtórzenia doklejoną BEZ spacji do ostatniego
 * słowa (pułapka 4 biblioteki: "Let it be, let it be" + " x4" → "...bex4"). */
function stripGluedRepeat(line: string): string {
  return line.replace(/x\d+\s*$/i, '').trimEnd();
}

/** true, gdy linia nie nadaje się na kotwicę tekstową: tabulatura, same cyfry,
 * pusta linia albo tekst wyglądający jak nagłówek sekcji. */
function isUselessAnchorLine(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed === '') return true;
  if (NO_CHORD_WORD_RE.test(trimmed)) return true;
  if (TAB_REMNANT_RE.test(trimmed)) return true;
  if (BRACKET_HEADER_LIKE_RE.test(trimmed)) return true;
  if (COLON_HEADER_LIKE_RE.test(trimmed)) return true;
  if (ORDINAL_HEADER_LIKE_RE.test(trimmed)) return true;
  if (REPEAT_ONLY_LINE_RE.test(trimmed)) return true;
  return false;
}

/**
 * Buduje krótką kotwicę tekstową (pierwsze 3-5 słów pierwszego użytecznego
 * wersu) z surowych `lyrics` sekcji, jednego wielowierszowego stringa (wiersze
 * rozdzielone `\n`). Obchodzi pułapkę 4: ChordSheetJS wycina zapis powtórzenia
 * (`x4`) z linii akordów i dokleja go BEZ SPACJI do `lyrics` ostatniego
 * elementu, więc naiwna kotwica wyszłaby jako `"Let it be, let it bex4"`.
 * Odrzuca linie tabulatury, same cyfry i tekst wyglądający jak nagłówek.
 * Zwraca `null`, gdy po oczyszczeniu nie zostaje nic sensownego do pokazania.
 */
export function buildAnchor(rawLyrics: string): string | null {
  for (const rawLine of rawLyrics.split('\n')) {
    const cleaned = stripGluedRepeat(rawLine);
    if (isUselessAnchorLine(cleaned)) continue;
    const words = cleaned.trim().split(/\s+/).filter((w) => w !== '');
    if (words.length === 0) continue;
    return words.slice(0, Math.min(5, words.length)).join(' ');
  }
  return null;
}

/**
 * Klucz scalania na poziomie degradacji 4: rodzaj sekcji plus jej progresja.
 * Liczona z `raw`, NIE z `base`, i zamrożona przez wywołującego przy pierwszym
 * zbudowaniu `ChartSection` (poziom 0), nigdy przeliczana przy degradacji: to
 * właśnie zamrożenie sprawia, że `C C G G` i `C G` mają różne sygnatury, mimo
 * że po zwinięciu sąsiadujących powtórzeń (poziom 4) obie dają `C G`. Gdyby
 * sygnaturę liczyć ponownie na wyjściu poziomu 4, ochrona przed scaleniem
 * (poziom 5) znikałaby dokładnie na tym poziomie, przed którym miała chronić.
 * Dwie sekcje tego samego typu o identycznej progresji dostają tę samą
 * sygnaturę; sekcje różnych typów nigdy się nie scalają, nawet przy
 * identycznych akordach (refren o akordach zwrotki nie jest zwrotką).
 */
export function buildSignature(kind: SectionKind, progression: PeriodResult): string {
  return `${kind}|${progression.raw.join(' ')}`;
}

/** Wejście dziedziczenia progresji: jedna sekcja w kolejności oryginalnej.
 * Sparametryzowane typem progresji (domyślnie pełne `Progression`), bo
 * `inheritProgressions` sama w sobie nie dotyka `phrases` - działa tak samo
 * na "gołym" wyniku `findSmallestPeriod` (`PeriodResult`), jak i na pełnej
 * progresji z frazowaniem. */
export interface InheritanceInput<P extends PeriodResult = Progression> {
  kind: SectionKind;
  /** Progresja policzona z własnych akordów sekcji (może być pusta, gdy ich brak). */
  progression: P;
  /** true, gdy sekcja ma choć jeden własny akord (nie dziedziczy). */
  hasOwnChords: boolean;
}

export interface InheritanceResult<P extends PeriodResult = Progression> {
  progression: P;
  /** true, gdy progresja pochodzi z wcześniejszej sekcji tego samego typu. */
  inherited: boolean;
}

/**
 * Sekcja bez własnych akordów dziedziczy progresję od ostatniej WCZEŚNIEJSZEJ
 * sekcji tego samego typu (`kind`). Realny przypadek: `nie-placz-ewka.txt` ma
 * drugą zwrotkę zapisaną jako sam tekst pod nagłówkiem "2nd:", bez jednego
 * akordu, bo autor założył, że gra się ją tak samo jak pierwszą. Gdy żadna
 * wcześniejsza sekcja tego typu nie ma własnych akordów, dziedziczenie nie ma
 * z czego skorzystać: sekcja zostaje z pustą progresją, `inherited: false`,
 * to woła o diagnostykę wyższego poziomu (poza zakresem tego modułu).
 * Dziedziczenie jest zawsze widoczne przez flagę `inherited`, nigdy ciche.
 */
export function inheritProgressions<P extends PeriodResult = Progression>(
  sections: readonly InheritanceInput<P>[],
): InheritanceResult<P>[] {
  const lastByKind = new Map<SectionKind, P>();
  const results: InheritanceResult<P>[] = [];

  for (const section of sections) {
    if (section.hasOwnChords) {
      lastByKind.set(section.kind, section.progression);
      results.push({ progression: section.progression, inherited: false });
      continue;
    }
    const inherited = lastByKind.get(section.kind);
    if (inherited) {
      results.push({ progression: inherited, inherited: true });
    } else {
      results.push({ progression: section.progression, inherited: false });
    }
  }

  return results;
}
